/**
 * Single source of truth for per-route SEO metadata.
 * Used by:
 *  - <SEO /> component at runtime (for client-rendered head updates)
 *  - scripts/build-seo-html.mjs at build time (to pre-render static HTML
 *    per public route so that crawlers and OG scrapers get correct tags
 *    without executing JavaScript)
 *
 * Add an entry here every time a new public, indexable route is added.
 */

export const SITE_URL = "https://email2contact.com";
export const SITE_NAME = "Email2Contact";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/default.png`;

export interface RouteSeo {
  /** Path beginning with "/" */
  path: string;
  /** <title> */
  title: string;
  /** <meta name="description"> */
  description: string;
  /**
   * Optional override for the OG / Twitter image URL.
   * If omitted and `/og/<slug>.png` is registered below, that file is used.
   * Otherwise DEFAULT_OG_IMAGE is used.
   */
  image?: string;
  /** Optional explicit OG slug (defaults to derived from path) */
  ogSlug?: string;
  /** Optional JSON-LD structured data blocks */
  jsonLd?: Record<string, unknown>[];
  /** Defaults to false (i.e. index, follow) */
  noindex?: boolean;
}

/** Routes that have a 1200x630 PNG present at `public/og/<slug>.png`. */
export const ROUTE_OG_SLUGS: Record<string, string> = {
  "/": "home",
  "/plans": "plans",
  "/pricing": "pricing",
  "/privacy": "privacy",
  "/terms": "terms",
  "/support": "support",
  "/refund-policy": "refund-policy",
  "/impressum": "impressum",
  // Other routes fall through to /og/default.png
};

const breadcrumb = (items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
  })),
});

export const ROUTES_SEO: Record<string, RouteSeo> = {
  "/": {
    path: "/",
    title: "Outlook Add-in: Email Signatures to Contacts | Email2Contact",
    description:
      "Email2Contact extracts email signatures in Outlook and saves complete contacts in one click. GDPR-compliant, EU-hosted AI, zero data retention.",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
        publisher: {
          "@type": "Organization",
          name: "Celsios GmbH",
          url: SITE_URL,
        },
        inLanguage: "en",
      },
    ],
  },
  "/plans": {
    path: "/plans",
    title: "Pricing — Email2Contact for Outlook",
    description:
      "Email2Contact pricing: Starter €5/mo, Pro €19/mo, Enterprise custom. Pay-as-you-go credit top-ups. GDPR-compliant, EU-hosted AI, cancel anytime.",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Pricing", path: "/plans" },
      ]),
    ],
  },
  "/pricing": {
    path: "/pricing",
    title: "Pricing Plans — Email2Contact for Outlook",
    description:
      "Compare Email2Contact pricing plans: Starter, Pro and Enterprise. Transparent EUR pricing, GDPR-compliant, EU-hosted AI, cancel anytime.",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Pricing", path: "/pricing" },
      ]),
    ],
  },
  "/support": {
    path: "/support",
    title: "Help & FAQ — Email2Contact for Outlook",
    description:
      "Get help with Email2Contact: how the Outlook add-in works, supported platforms, credit system, GDPR & EU AI hosting, billing, and how to contact support.",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Support", path: "/support" },
      ]),
    ],
  },
  "/privacy": {
    path: "/privacy",
    title: "Privacy & GDPR — Email2Contact",
    description:
      "Email2Contact privacy policy: 100% EU-hosted AI processing (Azure West Europe), zero data retention, GDPR-compliant, no AI training on your data.",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Privacy", path: "/privacy" },
      ]),
    ],
  },
  "/terms": {
    path: "/terms",
    title: "Terms of Service — Email2Contact",
    description:
      "Email2Contact Terms of Service for the Microsoft Outlook add-in. Provided by Celsios GmbH (Germany). License, acceptable use, billing, and liability.",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Terms", path: "/terms" },
      ]),
    ],
  },
  "/refund-policy": {
    path: "/refund-policy",
    title: "Refund & Cancelation Policy — Email2Contact",
    description:
      "Email2Contact refund and cancelation policy: EU 14-day rights, credit top-up rules, subscription cancelation, grace period, and dispute resolution.",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Refund Policy", path: "/refund-policy" },
      ]),
    ],
  },
  "/impressum": {
    path: "/impressum",
    title: "Impressum — Email2Contact (Celsios GmbH)",
    description:
      "Legal notice (Impressum) for Email2Contact under § 5 TMG. Operator: Celsios GmbH, Munich, Germany. HRB 134969. VAT ID DE442429324.",
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Impressum", path: "/impressum" },
      ]),
    ],
  },
};

/** Public-facing routes that should be pre-rendered as static HTML files. */
export const PRERENDER_ROUTES: string[] = [
  "/plans",
  "/pricing",
  "/support",
  "/privacy",
  "/terms",
  "/refund-policy",
  "/impressum",
];

export const resolveOgImage = (path: string, override?: string): string => {
  if (override) return override;
  const slug = ROUTE_OG_SLUGS[path];
  if (slug) return `${SITE_URL}/og/${slug}.png`;
  return DEFAULT_OG_IMAGE;
};
